import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../../common/types';
import { useBlockActions } from '../../hooks/useBlockActions';
import { useCampaignPeople, type PeopleFilter } from '../../hooks/useCampaignPeople';
import { useOutreachPlan } from '../../hooks/useOutreachPlan';
import type { PeopleRow as PeopleRowData } from '../../outreachTypes';
import { PeopleRow } from '../PeopleRow';
import { PitchDetailsDrawer } from '../details/PitchDetailsDrawer';
import { peopleDayLabel } from '../peopleDay';
import { PlugPulse } from './PlugPulse';
import { countdown, dayWord } from './upNextCopy';
import { useNow } from './useNow';
import { upNextMessages as messages } from './upNextMessages';
/* eslint-disable css-modules/no-unused-class -- the shared chips of the Outreach area and the stylesheets of Up next and People */
import shared from '../../Outreach.module.scss';
import styles from './UpNext.module.scss';
import peopleStyles from '../people/People.module.scss';
/* eslint-enable css-modules/no-unused-class */

const NO_FILTER: PeopleFilter = { kind: null, result: null };
/** The most rows of the card; the rest is in People. */
const MAX_ROWS = 6;

interface NextPickCardProps {
  accountId: number;
  campaignUuid: string;
  /** `YYYY-MM-DD` of the release, for the "release day" word; null when unknown. */
  releaseDay: string | null;
  /** ISO time of the next daily batch (`planned.nextBatchAt`). */
  nextBatchAt: string;
  /** The people that the pick takes; null while it loads. */
  count: number | null;
  onSeeAll: () => void;
}

/** The countdown ticks every second; only this card reads the clock that often. */
const SECOND_MS = 1000;

/**
 * Nothing is planned (design outreach-v2 t5, it replaces s4b): the people of the newest send day
 * with their live status (a row opens the details), and one line for the next pick with its
 * countdown. Before the first send the card shows only the next pick.
 */
const NextPickCardComponent: React.FC<NextPickCardProps> = ({
  accountId,
  campaignUuid,
  releaseDay,
  nextBatchAt,
  count,
  onSeeAll,
}) => {
  const intl = useIntl();
  const now = useNow(SECOND_MS);
  const { isDemo } = useOutreachPlan();
  const people = useCampaignPeople(accountId, campaignUuid, NO_FILTER);
  const actions = useBlockActions(accountId, campaignUuid);
  const [openedUuid, setOpenedUuid] = useState<string | null>(null);
  const close = useCallback(() => setOpenedUuid(null), []);
  const open = useCallback((row: PeopleRowData) => setOpenedUuid(row.messageUuid), []);
  const lastDay = useMemo(() => people.days.find((day) => day.sent > 0), [people.days]);
  const rows = useMemo(
    () => (lastDay?.rows ?? []).filter((row) => row.result !== 'scheduled').slice(0, MAX_ROWS),
    [lastDay]
  );
  // The panel reads the row of the newest list: a realtime read updates it, a blocked row closes it.
  const opened = useMemo(() => rows.find((row) => row.messageUuid === openedUuid) ?? null, [openedUuid, rows]);
  const time = intl.formatTime(new Date(nextBatchAt), { hour: '2-digit', minute: '2-digit' });
  const day = dayWord(intl, nextBatchAt, now);
  const left = countdown(nextBatchAt, now);

  return (
    <section className={styles.card} data-testid="up-next-pick">
      <div className={styles.head}>
        <h3 className={styles.title}>
          {lastDay === undefined
            ? intl.formatMessage(messages.title)
            : intl.formatMessage(messages.sentTitle, { day: peopleDayLabel(intl, lastDay.day, releaseDay) })}{' '}
          {lastDay !== undefined && (
            <span className={styles.suffix}>· {intl.formatMessage(messages.sentCount, { count: lastDay.sent })}</span>
          )}
        </h3>
        {lastDay !== undefined && (
          <button type="button" className={styles.link} onClick={onSeeAll} data-testid="up-next-see-all">
            {intl.formatMessage(messages.seeAll)}
          </button>
        )}
      </div>
      {rows.length > 0 && (
        <div className="py-1" data-testid="sent-today">
          {rows.map((row) => (
            <PeopleRow key={row.messageUuid} row={row} compact open={openedUuid === row.messageUuid} onOpen={open} />
          ))}
        </div>
      )}
      <div className={peopleStyles.next}>
        <PlugPulse size={40} idle />
        <div className={peopleStyles.nextText}>
          <b>
            {count === null || count === 0
              ? intl.formatMessage(messages.nextPickLineNoCount, { day, time })
              : intl.formatMessage(messages.nextPickLine, { count, day, time })}
          </b>
          <small>{intl.formatMessage(messages.nextPickSub)}</small>
        </div>
        {left !== null && (
          <span className={clsx(shared.chip, shared.chipVio, styles.countdown)} data-testid="up-next-countdown">
            {intl.formatMessage(messages.inTime, { time: left })}
          </span>
        )}
      </div>
      <PitchDetailsDrawer
        accountId={accountId}
        campaignUuid={campaignUuid}
        rows={rows}
        row={opened}
        actions={actions}
        disabled={isDemo}
        onSelect={open}
        onClose={close}
      />
    </section>
  );
};

export const NextPickCard = TypedMemo(NextPickCardComponent);
